import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CircleAlert, Lightbulb, Plus } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Caixas } from '../../componentes/caixas';
import { Formulario, type DefCampo } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { CampoVerticais, useMoedaDoFormulario, useMoedaSugerida, verticaisDaCriacao, type ValorVerticais } from '../../componentes/verticais';
import { ErroRequisicao, get, post } from '../../nucleo/api';
import { DialogoCriacao, useCriacaoDaChave } from '../../nucleo/criacao-rapida';
import type { ContextoCriacao, CriacaoNoModulo } from '../../nucleo/modulos-web';
import { nomePessoa } from '../../nucleo/formato';
import { opcoesMoeda } from '../../nucleo/paises';
import { rotuloEtapaPipeline, rotuloFunilPipeline } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import { caminhoDaNegociacao } from './crm-caminhos';
import { DicaPrincipal, MAX_OUTRAS_EMPRESAS, OutrosVinculos, comOutro, empresasDaCriacao, grupoParaMarcar, type Vinculo } from './CriarNegociacaoOutros';
import { useListas } from './crm-listas';
import { opcoesRegime, sugestaoDeRegime } from './crm-regime';
import { useFunis } from './Funis';
import type { Etapa } from './tipos';

/**
 * Ajustes do CRM 27/10 (C8): o formulário único de "Criar negociação" da casca nova (o `openDealForm` do clone do RD), usado
 * pelo Comercial, pelo botão flutuante (C6) e, na junção, pela empresa. Dica de personalizar cartões, nome, fonte, campanha,
 * funil e etapa (a etapa segue o funil), responsável, "Informações da empresa" e "Informações do contato" (cada uma com o
 * "Adicionar" que cria o cadastro sem sair daqui), verticais e os campos personalizados da negociação (lista de 03/11, item 2:
 * sem o atalho de criar campo). Botões Cancelar, "Salvar e criar outra" e Criar; ao criar, abre a negociação.
 *
 * Lista de 03/11, item 3: mais de uma empresa e mais de um contato. A empresa do campo é a principal (`parceiro_id`, o cliente da
 * negociação nos relatórios e no faturamento); "Outras empresas" entram como adicionais (`POST .../empresas`) e "Outros contatos"
 * junto com o contato escolhido e os sugeridos da empresa (`POST .../contatos`). O modo em lote segue de um item por negociação.
 *
 * Regra do servidor: a negociação tem um cliente (`parceiro_id`). É a empresa escolhida; sem empresa, o contato. O contato
 * escolhido também entra como contato principal da negociação (precisa de ler o Cadastros, como na ficha).
 */

const ID_FORM = 'form-criar-negociacao-crm';

/** O corpo do POST a partir dos valores (função pura, testada em `tests/crm-comercial-ajustes.test.ts`). */
export function corpoDaNegociacao(v: Record<string, unknown>): { erro: 'sem_cliente' } | { corpo: Record<string, unknown>; contato: string | null } {
  const txt = (x: unknown) => (typeof x === 'string' && x.trim() ? x.trim() : null);
  const empresa = txt(v.empresa_id);
  const contato = txt(v.contato_id);
  const parceiro = empresa ?? contato;
  if (!parceiro) return { erro: 'sem_cliente' };
  return {
    corpo: {
      titulo: String(v.titulo ?? '').trim(),
      parceiro_id: parceiro,
      valor_centavos: 0,
      // Lista de 04/11, item 3: a moeda escolhida (sem ela, a regra do servidor).
      ...(txt(v.moeda) ? { moeda: txt(v.moeda) } : {}),
      responsavel_id: txt(v.responsavel_id),
      origem: 'manual',
      utm_fonte: txt(v.utm_fonte),
      utm_campanha: txt(v.utm_campanha),
      ...(txt(v.funil_id) ? { funil_id: txt(v.funil_id) } : {}),
      ...(txt(v.etapa_id) ? { etapa_id: txt(v.etapa_id) } : {}),
      // Lote crm5 (E10): vazio não vai (o servidor sugere o regime em vigor da empresa de cada negociação).
      ...(txt(v.regime_pretendido) ? { regime_pretendido: txt(v.regime_pretendido) } : {}),
      extras: v.extras ?? {},
    },
    contato,
  };
}

/**
 * crm6 item 2: os contatos que entram na negociação. O escolhido no campo é o principal; os da empresa marcados entram
 * junto (o primeiro vira principal quando nenhum foi escolhido). Sem repetir. Função pura, testada em `tests/crm6-crm.test.ts`.
 */
export function contatosDaCriacao(escolhido: string | null, daEmpresa: string[]): { parceiro_id: string; principal: boolean }[] {
  const ids = [...new Set([...(escolhido ? [escolhido] : []), ...daEmpresa])];
  return ids.map((id, i) => ({ parceiro_id: id, principal: i === 0 }));
}

/**
 * rl0311-1 ROB-B2: Enter num campo dispara o clique no PRIMEIRO botão de envio do formulário, que aqui é o "Salvar e criar
 * outra". Só vale como "outra" o clique do mouse ou o teclado com o foco no próprio botão; Enter no campo salva como o primário.
 */
export const cliqueDeVerdade = (e: { detail: number; currentTarget: EventTarget }) => e.detail > 0 || document.activeElement === e.currentTarget;

export function CriarNegociacaoCrm({
  aoFechar,
  contexto,
  aoCriar,
  abrirAoCriar = true,
}: {
  aoFechar: () => void;
  contexto: ContextoCriacao;
  aoCriar?: (r: { id: string; nome?: string }) => void;
  abrirAoCriar?: boolean;
}) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const navegar = useNavigate();
  const funis = useFunis();
  const listas = useListas();
  const ativos = (funis.data?.itens ?? []).filter((f) => !f.arquivado_em);
  const funilInicial = contexto.funilId || ativos.find((f) => f.padrao)?.id || ativos[0]?.id || '';
  const [funilSel, setFunilSel] = useState<string>('');
  const funil = funilSel || funilInicial;
  const etapas = useQuery({ queryKey: ['pipeline', 'etapas', funil], queryFn: () => get<{ itens: Etapa[] }>('/pipeline/etapas', { funil_id: funil }), enabled: !!funil });
  const abertas = (etapas.data?.itens ?? []).filter((e) => e.tipo === 'aberta');
  const [n, setN] = useState(0);
  const [verticais, setVerticais] = useState<ValorVerticais>(null);
  const outra = useRef(false);
  // Empresa ou contato criados aqui mesmo ("Adicionar"): escolhidos no campo, com o nome no rótulo.
  const [criados, setCriados] = useState<{ empresa?: { id: string; nome: string }; contato?: { id: string; nome: string } }>({});
  // Lista de 03/11, item 3: as outras empresas (adicionais) e os outros contatos; `alvo` diz qual "Adicionar" abriu o cadastro rápido.
  const [outrasEmpresas, setOutrasEmpresas] = useState<Vinculo[]>([]);
  const [outrosContatos, setOutrosContatos] = useState<Vinculo[]>([]);
  const [alvo, setAlvo] = useState<'empresa' | 'contato' | 'outra-empresa' | 'outro-contato'>('empresa');
  const criarEmpresa = useCriacaoDaChave('empresa');
  const criarContato = useCriacaoDaChave('contato');
  const [adicionando, setAdicionando] = useState<CriacaoNoModulo | null>(null);
  // crm6 item 2: escolhida a empresa, os contatos dela aparecem marcados e entram na negociação ao criar.
  const [empresaSel, setEmpresaSel] = useState<string>(contexto.empresa?.id ?? '');
  const [contatoSel, setContatoSel] = useState<string>(contexto.contato?.id ?? '');
  const [desmarcados, setDesmarcados] = useState<ReadonlySet<string>>(new Set());
  const sugereContatos = s.pode('cadastros.ver') && s.pode('pipeline.editar');
  // Sem a empresa do campo, a primeira das outras é a principal (mesma regra do envio) e os contatos sugeridos são os dela.
  const empresaPrincipal = empresaSel || outrasEmpresas[0]?.id || '';
  const contatosEmpresa = useQuery({
    queryKey: ['crm', 'empresa', empresaPrincipal, 'contatos-da-criacao'],
    queryFn: () => get<{ itens: { id: string; nome: string }[] }>('/cadastros/parceiros', { principal_de: empresaPrincipal, papel: 'contato', limite: 50 }),
    enabled: !!empresaPrincipal && sugereContatos && !(contexto.lote ?? []).length,
  });
  // rl0211-1 SPEC-M4 e rl0211-4 ROB-B12: "negociações seguem pela principal" (decisão da lista de 02/11); o filtro `principal_de`
  // corta no servidor, antes do limite, quem tem a empresa só como adicional.
  const sugeridos = empresaPrincipal ? (contatosEmpresa.data?.itens ?? []).filter((c) => c.id !== contatoSel) : [];
  const marcados = sugeridos.filter((c) => !desmarcados.has(c.id)).map((c) => c.id);
  // Filiais (item 4 da ficha, decidido pelo Gustavo em 03/11): a empresa escolhida é matriz ou filial: as outras do grupo aparecem para
  // marcar, nenhuma marcada. Marcada entra como adicional; a escolhida segue principal. A marcação vale só para a principal em que foi feita.
  const [marcacaoGrupo, setMarcacaoGrupo] = useState<{ de: string; ids: string[] }>({ de: '', ids: [] });
  const grupo = useQuery({
    queryKey: ['crm', 'empresa', empresaPrincipal, 'grupo-da-criacao'],
    queryFn: () => get<{ itens: { id: string; nome: string }[]; proximo_cursor?: string | null }>('/cadastros/parceiros', { grupo_de: empresaPrincipal, papel: 'empresa', limite: 100 }),
    enabled: !!empresaPrincipal && sugereContatos && !(contexto.lote ?? []).length,
  });
  // Lista de 04/11, item 3 (D2-A): a moeda vem preenchida (vertical, cliente, workspace). Em lote, sem um cliente só, a escolhida vale para todas.
  const sugerida = useMoedaSugerida(verticais, (contexto.lote ?? []).length ? null : empresaPrincipal || contatoSel || null);
  const moedaForm = useMoedaDoFormulario(sugerida);
  const [moedaVista, setMoedaVista] = useState(sugerida);
  const noGrupo = grupoParaMarcar(grupo.data?.itens ?? [], outrasEmpresas);
  const marcadasGrupo = marcacaoGrupo.de === empresaPrincipal ? marcacaoGrupo.ids.filter((x) => noGrupo.some((g) => g.id === x)) : [];

  const opcoesLista = (itens: { nome: string }[] | undefined) => (itens ?? []).map((x) => ({ valor: x.nome, rotulo: x.nome }));
  const lote = contexto.lote ?? [];
  const emLote = lote.length > 0;
  const nomeDoItem = (i: (typeof lote)[number]) => (i.contato ?? i.empresa)?.nome ?? '';
  // rcrm4 ROB-M3 e SPEC-B3: cada item do lote (ou o formulário único) tem um id gerado UMA vez; repetir o envio reusa o id
  // (o 409 do mesmo id conta como feito), e o item criado sai do lote, para a repetição mandar só o que falta.
  const chaveItem = (i: (typeof lote)[number]) => `${n}:${i.empresa?.id ?? ''}:${i.contato?.id ?? ''}`;
  const idsEstaveis = useRef(new Map<string, string>());
  const idDe = (chave: string) => {
    let id = idsEstaveis.current.get(chave);
    if (!id) idsEstaveis.current.set(chave, (id = crypto.randomUUID()));
    return id;
  };
  const [feitos, setFeitos] = useState<ReadonlySet<string>>(new Set());
  const pendentes = lote.filter((i) => !feitos.has(chaveItem(i)));
  const campos: DefCampo[] = [
    { nome: 'titulo', rotulo: t('crm.criarNeg.nome'), tipo: 'texto', obrigatorio: true, placeholder: t('crm.criarNeg.nomePlaceholder') },
    listas.data?.fontes.length
      ? { nome: 'utm_fonte', rotulo: t('crm.criarNeg.fonte'), tipo: 'selecao', opcoes: opcoesLista(listas.data.fontes), largura: 'meia' }
      : { nome: 'utm_fonte', rotulo: t('crm.criarNeg.fonte'), tipo: 'texto', largura: 'meia' },
    listas.data?.campanhas.length
      ? { nome: 'utm_campanha', rotulo: t('crm.criarNeg.campanha'), tipo: 'selecao', opcoes: opcoesLista(listas.data.campanhas), largura: 'meia' }
      : { nome: 'utm_campanha', rotulo: t('crm.criarNeg.campanha'), tipo: 'texto', largura: 'meia' },
    { nome: 'funil_id', rotulo: t('crm.criarNeg.funil'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: ativos.map((f) => ({ valor: f.id, rotulo: rotuloFunilPipeline(f.nome) })), limpa: ['etapa_id'] },
    { nome: 'etapa_id', rotulo: t('crm.criarNeg.etapa'), tipo: 'selecao', largura: 'meia', opcoes: abertas.map((e) => ({ valor: e.id, rotulo: rotuloEtapaPipeline(e.nome) })), ajuda: t('crm.criarNeg.etapaAjuda') },
    { nome: 'responsavel_id', rotulo: t('crm.criarNeg.responsavel'), tipo: 'busca', busca: { caminho: '/membros', rotulo: (i) => String(i.nome), pessoa: true } },
    { nome: 'regime_pretendido', rotulo: t('campos.regimePretendido'), tipo: 'selecao', largura: 'meia', opcoes: opcoesRegime(), ajuda: t('campos.regimePretendidoAjuda') },
    { nome: 'moeda', rotulo: t('campos.moeda'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: opcoesMoeda(moedaVista) },
    ...(emLote
      ? []
      : ([
      {
        nome: 'empresa_id',
        rotulo: t('crm.criarNeg.empresa'),
        tipo: 'busca',
        grupo: t('crm.criarNeg.grupoEmpresa'),
        busca: { caminho: '/cadastros/parceiros', params: { papel: 'empresa' }, rotulo: (i) => String(i.nome) },
        // RCRM5-SEG-M1: o regime da empresa é dado do Cadastros (só com a leitura dele); SPEC-B3: a sugestão segue a empresa.
        ...(s.pode('cadastros.ver') ? { preencher: sugestaoDeRegime, preencherSugere: true } : {}),
      },
      {
        nome: 'contato_id',
        rotulo: t('crm.criarNeg.contato'),
        tipo: 'busca',
        grupo: t('crm.criarNeg.grupoContato'),
        busca: { caminho: '/cadastros/parceiros', params: { papel: 'contato' }, rotulo: (i) => String(i.nome), pessoa: true },
      },
        ] satisfies DefCampo[])),
  ];
  const inicial: Record<string, unknown> = {
    titulo: emLote ? (lote.length === 1 ? nomeDoItem(lote[0]!) : '') : (contexto.empresa?.nome ?? contexto.contato?.nome ?? ''),
    funil_id: funilInicial,
    moeda: sugerida,
    responsavel_id: s.usuario.id,
    empresa_id: contexto.empresa?.id ?? null,
    contato_id: contexto.contato?.id ?? null,
  };
  const rotulos: Record<string, string> = { responsavel_id: s.usuario.nome };
  const empresaNome = criados.empresa?.nome ?? contexto.empresa?.nome;
  const contatoNome = criados.contato?.nome ?? contexto.contato?.nome;
  if (empresaNome) rotulos.empresa_id = empresaNome;
  if (contatoNome) rotulos.contato_id = contatoNome;
  const sobrepor = criados.empresa || criados.contato ? { ...(criados.empresa ? { empresa_id: criados.empresa.id } : {}), ...(criados.contato ? { contato_id: criados.contato.id } : {}) } : undefined;

  const botaoAdicionar = (c: CriacaoNoModulo | null, rotulo: string, dado: 'empresa' | 'contato') =>
    c && (
      <div className="sm:col-span-6">
        <button type="button" data-adicionar={dado} onClick={() => (setAlvo(dado), setAdicionando(c))} className="flex min-h-11 items-center gap-1.5 text-sm font-medium text-acento-forte hover:underline md:min-h-9">
          <Plus className="size-4" aria-hidden />
          {rotulo}
        </button>
      </div>
    );

  return (
    <>
      <Drawer
        aberto
        aoFechar={aoFechar}
        largura="lg"
        titulo={t('crm.comercial.criarNegociacao')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="secundario" type="submit" form={ID_FORM} data-salvar-e-criar-outra="" onClick={(e) => (outra.current = cliqueDeVerdade(e))}>
              {t('crm.criarNeg.salvarOutra')}
            </Botao>
            <Botao variante="primario" type="submit" form={ID_FORM} data-criar-negociacao="" onClick={() => (outra.current = false)}>
              {t('crm.criarNeg.criar')}
            </Botao>
          </>
        }
      >
        <div className="mb-4 flex gap-3 rounded-lg border border-borda bg-sup-2 px-3 py-2.5 text-sm" data-dica-cartoes="">
          <Lightbulb className="mt-0.5 size-4 shrink-0 text-dourado" aria-hidden />
          <p className="text-tinta-2">
            <span className="font-semibold text-tinta-1">{t('crm.comercial.personalizarCartoes')}</span>
            <br />
            {t('crm.criarNeg.dicaCartoes')}
          </p>
        </div>
        {emLote && lote.length > 1 && (
          <p className="mb-4 text-sm text-tinta-3" data-lote-nomes="">
            {pendentes.map((i) => nomePessoa(nomeDoItem(i))).join(', ')}
          </p>
        )}
        <Formulario
          key={`${n}-${funilInicial}`}
          id={ID_FORM}
          campos={campos}
          entidade="pipeline.oportunidade"
          inicial={inicial}
          rotulosAtuais={rotulos}
          sobrepor={sobrepor}
          acompanhar={moedaForm.acompanhar}
          aoMudar={(v) => {
            setMoedaVista(typeof v.moeda === 'string' && v.moeda ? v.moeda : sugerida);
            moedaForm.observar(v);
            setFunilSel(typeof v.funil_id === 'string' ? v.funil_id : '');
            const emp = typeof v.empresa_id === 'string' ? v.empresa_id : '';
            if (emp !== empresaSel) {
              setEmpresaSel(emp);
              setDesmarcados(new Set());
            }
            setContatoSel(typeof v.contato_id === 'string' ? v.contato_id : '');
          }}
          fimDoGrupo={emLote ? undefined : {
            [t('crm.criarNeg.grupoEmpresa')]: (
              <>
                {botaoAdicionar(criarEmpresa, t('crm.criarNeg.adicionarEmpresa'), 'empresa')}
                {sugereContatos && (
                  <>
                    <DicaPrincipal />
                    {noGrupo.length > 0 && (
                      <div className="sm:col-span-6" data-empresas-do-grupo="">
                        <Caixas
                          id="criar-neg-empresas-do-grupo"
                          rotulo={t('crm.criarNeg.empresasDoGrupo')}
                          itens={noGrupo}
                          valor={marcadasGrupo}
                          aoMudar={(ids) => {
                            // O teto é o das adicionais: a marcação a mais não entra, e o aviso diz por quê.
                            if (ids.length + outrasEmpresas.length > MAX_OUTRAS_EMPRESAS) return toast.info(t('crm.criarNeg.empresasDoGrupoLimite', { n: MAX_OUTRAS_EMPRESAS }));
                            setMarcacaoGrupo({ de: empresaPrincipal, ids });
                          }}
                          pessoas
                        />
                        <p className="mt-1 text-xs text-tinta-3">{t(grupo.data?.proximo_cursor ? 'crm.criarNeg.empresasDoGrupoTruncado' : 'crm.criarNeg.empresasDoGrupoAjuda')}</p>
                      </div>
                    )}
                    {grupo.isError && (
                      <p role="alert" className="flex items-start gap-1 text-xs text-perigo sm:col-span-6" data-grupo-erro="">
                        <CircleAlert className="mt-px size-3.5 shrink-0" aria-hidden />
                        <span className="min-w-0">{t('crm.criarNeg.empresasDoGrupoErro')}</span>
                      </p>
                    )}
                    <OutrosVinculos
                      tipo="empresa"
                      itens={outrasEmpresas}
                      aoAdicionar={(v) => setOutrasEmpresas((l) => comOutro(l, v, new Set(empresaSel ? [empresaSel, ...marcadasGrupo] : marcadasGrupo)))}
                      aoTirar={(id) => setOutrasEmpresas((l) => l.filter((x) => x.id !== id))}
                      jaTem={new Set(empresaSel ? [empresaSel, ...marcadasGrupo] : marcadasGrupo)}
                      busca={{ caminho: '/cadastros/parceiros', params: { papel: 'empresa' }, rotulo: (i) => String(i.nome) }}
                      criar={criarEmpresa ? () => (setAlvo('outra-empresa'), setAdicionando(criarEmpresa)) : undefined}
                      limite={MAX_OUTRAS_EMPRESAS - marcadasGrupo.length}
                    />
                  </>
                )}
              </>
            ),
            [t('crm.criarNeg.grupoContato')]: (
              <>
                {sugeridos.length > 0 && (
                  <div className="sm:col-span-6" data-contatos-da-empresa="">
                    <Caixas
                      id="criar-neg-contatos-empresa"
                      rotulo={t('crm.criarNeg.contatosDaEmpresa')}
                      itens={sugeridos}
                      valor={marcados}
                      aoMudar={(v) => setDesmarcados(new Set(sugeridos.filter((c) => !v.includes(c.id)).map((c) => c.id)))}
                      pessoas
                    />
                    <p className="mt-1 text-xs text-tinta-3">{t('crm.criarNeg.contatosDaEmpresaAjuda')}</p>
                  </div>
                )}
                {botaoAdicionar(criarContato, t('crm.criarNeg.criarContato'), 'contato')}
                {sugereContatos && (
                  <OutrosVinculos
                    tipo="contato"
                    itens={outrosContatos}
                    aoAdicionar={(v) => setOutrosContatos((l) => comOutro(l, v, new Set(contatoSel ? [contatoSel] : [])))}
                    aoTirar={(id) => setOutrosContatos((l) => l.filter((x) => x.id !== id))}
                    jaTem={new Set(contatoSel ? [contatoSel] : [])}
                    busca={{ caminho: '/cadastros/parceiros', params: { papel: 'contato' }, rotulo: (i) => String(i.nome), pessoa: true }}
                    pessoa
                    criar={criarContato ? () => (setAlvo('outro-contato'), setAdicionando(criarContato)) : undefined}
                  />
                )}
              </>
            ),
          }}
          aoEnviar={async (v) => {
            // Em lote (crm4 L2): uma negociação por item da seleção; o nome ganha o do item quando são vários. Só os que
            // faltam (os criados numa tentativa anterior já saíram), cada um com o seu id estável.
            // Item 3 (03/11): a empresa do campo é a principal; sem ela, a primeira das outras. Sem contato escolhido, o primeiro dos outros.
            const emp = emLote ? { principal: null, adicionais: [] as string[] } : empresasDaCriacao(typeof v.empresa_id === 'string' && v.empresa_id ? v.empresa_id : null, [...outrasEmpresas.map((x) => x.id), ...marcadasGrupo]);
            const itens = emLote
              ? pendentes.map((i) => ({ chave: chaveItem(i), v: { ...v, titulo: lote.length > 1 ? `${String(v.titulo ?? '').trim()} - ${nomeDoItem(i)}`.slice(0, 200) : v.titulo, empresa_id: i.empresa?.id ?? null, contato_id: i.contato?.id ?? null } }))
              : [{ chave: `${n}:unico`, v: { ...v, empresa_id: emp.principal, contato_id: (typeof v.contato_id === 'string' && v.contato_id ? v.contato_id : null) ?? outrosContatos[0]?.id ?? null } }];
            const total = emLote ? lote.length : 1;
            let criadas = emLote ? lote.length - pendentes.length : 0;
            const novosFeitos = new Set(feitos);
            let ultimo = { id: '', nome: '' };
            try {
              for (const { chave, v: item } of itens) {
                const r = corpoDaNegociacao(item);
                if ('erro' in r) throw new Error(t('crm.criarNeg.semCliente'));
                const id = idDe(chave);
                try {
                  await post('/pipeline/oportunidades', { id, ...r.corpo, ...verticaisDaCriacao(verticais) });
                } catch (e) {
                  // A tentativa anterior criou e a resposta se perdeu: o mesmo id já existe, então este item está feito.
                  if (!(e instanceof ErroRequisicao && e.status === 409 && e.corpo.code === 'registro.duplicado')) throw e;
                }
                criadas++;
                novosFeitos.add(chave);
                ultimo = { id, nome: r.corpo.titulo as string };
                const contatos = contatosDaCriacao(r.contato, emLote ? [] : [...marcados, ...outrosContatos.map((x) => x.id)]);
                // rl0311-1 ROB-B1: repetir depois de resposta perdida (409) também completa contatos e empresas. O formulário pode ter mudado
                // entre as tentativas; o servidor não duplica o vínculo.
                if (contatos.length && s.pode('pipeline.editar')) {
                  // O contato entra como principal (crm6: e os da empresa marcados, junto); se não der (sem leitura do
                  // Cadastros), a negociação já existe: só avisa.
                  let falhou = false;
                  for (const c of contatos) await post(`/pipeline/oportunidades/${id}/contatos`, c).catch(() => (falhou = true));
                  if (falhou) toast.info(t('crm.criarNeg.contatoNaoEntrou'));
                }
                // Item 3 (03/11): as empresas adicionais entram depois da negociação, como os contatos; se não der, a negociação já existe.
                if (!emLote && emp.adicionais.length && s.pode('pipeline.editar')) {
                  let falhouEmpresa = false;
                  for (const parceiro_id of emp.adicionais) await post(`/pipeline/oportunidades/${id}/empresas`, { parceiro_id }).catch(() => (falhouEmpresa = true));
                  if (falhouEmpresa) toast.info(t('crm.criarNeg.empresaNaoEntrou'));
                }
              }
            } catch (e) {
              setFeitos(novosFeitos);
              await qc.invalidateQueries({ queryKey: ['pipeline'] });
              // Só com algo criado há o que reler na ficha e na lista (rcn-4, ROB-M1).
              if (criadas) {
                void qc.invalidateQueries({ queryKey: ['crm'] });
                void qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
              }
              if (emLote && criadas) toast.erro(t('crm.criarNeg.parcial', { feitos: criadas, total }));
              throw e;
            }
            toast.sucesso(t(emLote ? 'crm.criarNeg.criadas' : 'crm.criarNeg.criada', { count: criadas }));
            void qc.invalidateQueries({ queryKey: ['pipeline'] });
            // Rodada rcn-2 (ROB-B1): pelo botão flutuante, com "Salvar e criar outra", a ficha segue aberta e atualiza; a coluna da lista também.
            // Sem esperar a releitura: o formulário fecha na hora (rcn-3, ROB-B1).
            void qc.invalidateQueries({ queryKey: ['crm'] });
            void qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
            aoCriar?.(ultimo);
            if (outra.current) {
              moedaForm.reiniciar();
              setCriados({});
              setMarcacaoGrupo({ de: '', ids: [] });
              setOutrasEmpresas([]);
              setOutrosContatos([]);
              setFunilSel('');
              setEmpresaSel(contexto.empresa?.id ?? '');
              setContatoSel(contexto.contato?.id ?? '');
              setDesmarcados(new Set());
              setFeitos(new Set());
              setN((x) => x + 1);
              return;
            }
            aoFechar();
            if (abrirAoCriar && !emLote) navegar(caminhoDaNegociacao(ultimo.id));
          }}
        >
          <CampoVerticais valor={verticais} aoMudar={setVerticais} />
        </Formulario>
      </Drawer>
      <DialogoCriacao
        aberta={adicionando}
        aoFechar={() => setAdicionando(null)}
        contexto={{}}
        aoCriar={async (c) => {
          const nome =
            c.nome ??
            (await get<{ nome: string }>(`/cadastros/parceiros/${c.id}`)
              .then((p) => p.nome)
              .catch(() => ''));
          // Item 3 (03/11): o cadastro criado vai ao campo principal ou ao fim da lista dos outros, conforme o "Adicionar" que abriu.
          if (alvo === 'outra-empresa') setOutrasEmpresas((l) => comOutro(l, { id: c.id, nome }, new Set(empresaSel ? [empresaSel] : [])));
          else if (alvo === 'outro-contato') setOutrosContatos((l) => comOutro(l, { id: c.id, nome }, new Set(contatoSel ? [contatoSel] : [])));
          else setCriados((x) => ({ ...x, [alvo === 'contato' ? 'contato' : 'empresa']: { id: c.id, nome } }));
        }}
      />
    </>
  );
}
